import { useCallback, useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { Address } from 'src/components/checkout/steps/shipping/types'
import type { OrderForm } from 'src/sdk/checkout/entity/orderForm'
import { fetchCepData } from 'src/utils/fetchCepData'
import { setCookie } from 'src/utils/cookies'
import { useScrollBlock } from 'src/utils/useScrollBlock'

import { usePostalCodeDefaultAddress } from './usePostalCodeDefaultAddress'
import { usePostalCodeModalTracking } from './usePostalCodeModalTracking'

interface UsePostalCodeModalControllerParams {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  orderForm: OrderForm
}

interface CloseModalArgs {
  shouldTrackClose: boolean
  hasInputValue?: boolean
  buttonName?: string
  skipTracking?: boolean
}

interface PostalCodeSubmitOptions {
  shouldTrackConfirmation?: boolean
  source?: string
  modalName?: string
}

export function usePostalCodeModalController({
  showModal,
  setShowModal,
  orderForm,
}: UsePostalCodeModalControllerParams) {
  const [inputValue, setInputValue] = useState('')
  const [provisionalAddress, setProvisionalAddress] = useState<Address | null>(
    null
  )

  const { defaultAddressId, setDefaultAddressId } = usePostalCodeDefaultAddress(
    {
      showModal,
      orderFormId: orderForm.orderFormId,
    }
  )

  const { trackModalClose, trackPostalCodeConfirmed } =
    usePostalCodeModalTracking()

  const hasConfirmedCepRef = useRef(false)

  const { allowScroll, blockScroll } = useScrollBlock()

  useEffect(() => {
    if (showModal) {
      hasConfirmedCepRef.current = false
    }
  }, [showModal])

  useEffect(() => {
    showModal ? blockScroll() : allowScroll()

    return () => {
      allowScroll()
    }
  }, [allowScroll, blockScroll, showModal])

  const onModalClose = useCallback(
    ({
      shouldTrackClose,
      hasInputValue,
      buttonName,
      skipTracking,
    }: CloseModalArgs) => {
      const inputDigitsCount = inputValue.replace(/\D/g, '').length
      const hasCompletePostalCode = inputDigitsCount >= 8
      const hasCompletePostalCodeInput =
        (hasInputValue ?? inputDigitsCount > 0) && hasCompletePostalCode

      const resolvedSkipTracking = skipTracking ?? hasConfirmedCepRef.current

      allowScroll()
      setCookie('isPostalCodeDismissed', 'true', {
        expiresInMinutes: 24 * 60,
      })
      setShowModal(false)

      trackModalClose({
        shouldTrackClose,
        hasInputValue: hasCompletePostalCodeInput,
        buttonName,
        skipTracking: resolvedSkipTracking,
      })

      document.documentElement.classList.remove('no-scroll')
    },
    [allowScroll, inputValue, setShowModal, trackModalClose]
  )

  const onSubmitPostalCode = useCallback(
    async (value: string, options: PostalCodeSubmitOptions = {}) => {
      const cepData = await fetchCepData(value)

      if (!cepData) {
        setProvisionalAddress({ postalCode: value })

        return
      }

      setProvisionalAddress(cepData)

      if (options.shouldTrackConfirmation) {
        await trackPostalCodeConfirmed(value, {
          source: options.source,
          modalName: options.modalName,
        })
        hasConfirmedCepRef.current = true
      }
    },
    [trackPostalCodeConfirmed]
  )

  const onConfirmGuestPostalCode = useCallback(
    async (value: string) => {
      await trackPostalCodeConfirmed(value, {
        source: 'Endereco do convidado',
      })
      hasConfirmedCepRef.current = true
    },
    [trackPostalCodeConfirmed]
  )

  return {
    inputValue,
    setInputValue,
    defaultAddressId,
    setDefaultAddressId,
    provisionalAddress,
    setProvisionalAddress,
    onModalClose,
    onSubmitPostalCode,
    onConfirmGuestPostalCode,
  }
}
